import _cloneDeep from 'lodash/cloneDeep';
import _merge from 'lodash/merge';
import _debounce from 'lodash/debounce';

import EventBus from '@/event-bus';
import PriceKinds from '@/product-instance/price-kinds';
import Variants from '@/product-instance/variants';
import Quantity from '@/product-instance/quantity';
import OptionSelector from '@/option-selector';
import getCartItemAccessoriesData from '@/helpers/getCartItemAccessoriesData';
import perfDebug from '@/helpers/perfDebug';

/** @private */
var AWAIT_TIME = 200;

/**
 * @param {{ deferInitialUpdate?: boolean }} [instanceOpts]
 *   `deferInitialUpdate: true` — первый `update()` в microtask (путь `_initInstanceAsync`); иначе синхронно (тесты / `Product` без defer).
 */
var ProductInstance = function(instanceConfig, $node, instanceOpts) {
  var self = this;
  instanceOpts = instanceOpts || {};
  var deferInitialUpdate = Boolean(instanceOpts.deferInitialUpdate);

  // Общие поля (product, tree, variants…) шарим по ссылке; глубоко копируем только то, что мутируется
  // per-instance: settings и навигация по опциям (InstanceOptions пишет selected/renderType в option).
  var perf = perfDebug.isEnabled();
  var tClone0 = perf ? perfDebug.now() : 0;
  var config = Object.assign({}, instanceConfig);
  config.settings = _cloneDeep(instanceConfig.settings);
  config.options = _cloneDeep(instanceConfig.options);
  config.node = $node;
  self._perfConfigCloneMs = perf ? perfDebug.now() - tClone0 : 0;

  /** @type {Record<string, number>|null} */
  var phases = perf
    ? {
      priceKindsMs: 0,
      variantsMs: 0,
      quantityMs: 0,
      optionSelectorMs: 0,
      updateMs: 0,
      bindEventsMs: 0,
    }
    : null;

  // прибиваем экземпляр к узлу
  self.$product = config.node; // eslint-disable-line id-length
  self.$product[0].Product = self;

  self.id = config.instanceId;
  self.product = config.product;

  config.settings.options = _merge(
    config.settings.options,
    self._getOptionsFromAttr(config.settings.selectors.options),
  );

  // настройки для экземпляра
  self.settings = config.settings;
  self.selectors = config.settings.selectors.product;

  self.type = self._getType();
  self.currentVariantId = null;

  var t0;
  t0 = perf ? perfDebug.now() : 0;
  self.price_kinds = new PriceKinds(config.price_kinds);
  if (perf) {
    phases.priceKindsMs = Math.round((perfDebug.now() - t0) * 100) / 100;
  }

  t0 = perf ? perfDebug.now() : 0;
  self.variants = new Variants(config);
  if (perf) {
    phases.variantsMs = Math.round((perfDebug.now() - t0) * 100) / 100;
  }

  // инитим кол-во товаров
  t0 = perf ? perfDebug.now() : 0;
  self.quantity = new Quantity(config, self.$product);
  if (perf) {
    phases.quantityMs = Math.round((perfDebug.now() - t0) * 100) / 100;
  }

  // запускаем селектор модификаций
  t0 = perf ? perfDebug.now() : 0;
  self.optionSelector = new OptionSelector(config, self.variants, self._getVariantId());
  if (perf) {
    phases.optionSelectorMs = Math.round((perfDebug.now() - t0) * 100) / 100;
  }

  self._perfPhases = phases;
  self._initialUpdatePromise = null;

  if (deferInitialUpdate) {
    t0 = perf ? perfDebug.now() : 0;
    self.bindEvents();
    if (perf) {
      phases.bindEventsMs = Math.round((perfDebug.now() - t0) * 100) / 100;
    }

    self._initialUpdatePromise = Promise.resolve().then(function() {
      var tu0 = perf ? perfDebug.now() : 0;
      self.update();
      if (perf && phases) {
        phases.updateMs = Math.round((perfDebug.now() - tu0) * 100) / 100;
      }
    });
  } else {
    t0 = perf ? perfDebug.now() : 0;
    self.update();
    if (perf) {
      phases.updateMs = Math.round((perfDebug.now() - t0) * 100) / 100;
    }

    t0 = perf ? perfDebug.now() : 0;
    self.bindEvents();
    if (perf) {
      phases.bindEventsMs = Math.round((perfDebug.now() - t0) * 100) / 100;
    }
  }

  return self;
};

/**
 * Устанавливаем новые настройки
 * @return {void}
 */
ProductInstance.prototype.setConfig = function(settings) {
  var self = this;

  self.settings = settings;

  self.variants.setConfig(self.settings);
  self.quantity.setConfig(self.settings);
  self.optionSelector.setConfig(self.settings);

  return;
};

/** @private */
ProductInstance.prototype._getOptionsFromAttr = function(selectors) {
  var self = this;
  var $nativeSelect = self.$product.find(selectors.native.css);
  var result = {};

  try {
    var result = $nativeSelect.data(selectors.native.data) || {};
  } catch (e) {
    return result;
  }

  return result;
};

/**
 * @private
 *
 * @description
 * Определяем тип инстанса
 *
 * @return {Strin} тип инстанса
 */
ProductInstance.prototype._getType = function() {
  var self = this;
  var _type = 'product';

  if (self.$product.data(self.selectors.item.data)) {
    _type = 'item';
  }

  return _type;
};

/**
 *
 */
ProductInstance.prototype.update = function(instance) {
  var self = this;
  var _variant;
  var _quantity;
  var _prices;
  var _atCart;
  var _quantityStatus;

  if (instance) {
    self = instance;
  }

  var currentVariantId = self._getVariantId();
  _variant = self.variants.getVariant(currentVariantId);

  /**
   * @private
   * Проверем, есть ли такой товар в корзине
   *
   * @note: пока этот флаг никуда не вынесен. Заготовка.
   */
  _atCart = InsalesCommonCart.order.getItemByID(_variant.id);

  if (_atCart && self.settings.withCart && self.type != 'item') {
    _quantity += _atCart.quantity;
  }

  self.quantity.setVariant(_variant);
  _quantity = self.quantity.get();

  _prices = self.price_kinds.get({
    variantId: _variant.id,
    quantity: _quantity.current,
  });

  if (self.optionSelector) {
    self.optionSelector.update(_variant.id);
  }

  _quantityStatus = self.quantity.getStatus();

  let warehouse_id = self.quantity.getInput().parents('form:first').data('warehouseId');

  // Если есть аксессуары, то получаем данные о них
  const accessoriesData = getCartItemAccessoriesData(self.$product[0]);

  _variant.action = _merge({
    method: 'update',
    product: self.$product,
    price: _prices,
    quantity: _quantity,
    quantityState: _quantityStatus,
    quantityInput: self.quantity.getInput(),
    productJSON: self.product,
    productInstance: self.$product,
    warehouse_id,
    accessory_value_ids: accessoriesData
  }, self.variants.getStatus());

  EventBus.publish('before:insales:' + self.type, _variant);

  if (_quantityStatus.change) {
    // вызов getInput обязателен, иначе теряется контекст quantityInput
    self.quantity.getInput();
    EventBus.publish('change_quantity:insales:' + self.type, _variant);
    self.$product.trigger('change_quantity:insales');
  } else if (_quantityStatus.more || _quantityStatus.less) {
    EventBus.publish('unchange_quantity:insales' + self.type, _variant);
  }

  if (_variant.id != self.currentVariantId) {
    EventBus.publish('change_variant:insales:' + self.type, _variant);
    self.currentVariantId = _variant.id;
  }

  EventBus.publish('update_variant:insales:' + self.type, _variant);

  EventBus.publish('always:insales:' + self.type, _variant);
};

/**
 *
 */
ProductInstance.prototype.bindEvents = function() {
  var self = this;

  var _debounsedUpdate = _debounce(self.update, AWAIT_TIME);

  EventBus.subscribe('product_instance:insales:system', function(eventData) {
    if (self.id == eventData.instanceId) {
      _debounsedUpdate(self);
    }
  });

  return;
};

/**
 * @private
 */
ProductInstance.prototype._getVariantId = function() {
  var self = this;
  var _variant_id = null;

  if (self.type == 'item') {
    _variant_id = self.$product.data(self.selectors.item.data);
  } else {
    _variant_id = self.quantity.getVariantFromNode();
  }

  return _variant_id;
};

export default ProductInstance;

perfDebug.logModuleEval('@/product-instance/index');
